import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import { useInfoStore } from '@/stores/info.ts'
import { usePreferencesStore } from '@/stores/preferences.ts'
import { useUserSettingsStore } from '@/stores/user-settings'
import { sendError } from '@/utils/sendError.ts'

export async function requestPushPermission(): Promise<boolean> {
  const { receive } = await PushNotifications.checkPermissions()
  if (receive === 'granted') return true
  const result = await PushNotifications.requestPermissions()
  return result.receive === 'granted'
}

export function storeFcmTokenWhenAvailable(): void {
  const { fcmToken } = storeToRefs(useInfoStore())
  const settingsStore = useUserSettingsStore()
  if (fcmToken.value) {
    settingsStore.addFcmToken(fcmToken.value)
    return
  }
  const stopWatching = watch(fcmToken, (token) => {
    if (!token) return
    settingsStore.addFcmToken(token)
    stopWatching()
  })
}

export async function enablePushNotificationsForDevice(): Promise<boolean> {
  const granted = await requestPushPermission()
  if (!granted) return false
  await PushNotifications.register()
  storeFcmTokenWhenAvailable()
  return true
}

export async function enablePushNotificationsOnFirstStart(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  const preferences = usePreferencesStore()
  if (preferences.pushAutoEnableAttempted) return
  const { receive } = await PushNotifications.checkPermissions()
  if (receive === 'denied') return
  await preferences.setPushAutoEnableAttempted(true)
  try {
    await enablePushNotificationsForDevice()
  } catch (e) {
    sendError(e, { source: 'enablePushNotificationsOnFirstStart' })
  }
}
